// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QtyField, { QtyFieldStyles } from "@synd/App/Components/Qty/QtyField";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    OptionObject,
} from "@synd/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
    // XNG-Changes
    disabled?: boolean;
    hideActions?: boolean;
    onClearProduct?: () => void;
    showSearchIcon: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
        // XNG-Changes
        isPriceManagementPage: getCurrentPage(state).type === "RetailerPricingManagementPage",
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    unitOfMeasureGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    clearIcon?: IconProps;
    // XNG-Changes
    qtyFieldStyle?: QtyFieldStyles;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        gap: 10,
    },
    searchGridItem: {
        width: [12, 12, 4, 5, 5],
        css: css`
            position: relative;
        `,
    },
    qtyGridItem: {
        width: [3, 3, 2, 1, 1],
    },
    unitOfMeasureGridItem: {
        width: [9, 9, 3, 3, 3],
    },
    buttonGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    selectButton: {
        css: css`
            margin-top: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    clearIcon: {
        css: css`
            position: absolute;
            right: 40px;
            top: 45px;
            cursor: pointer;
        `,
    },
};

const ENTER_KEY = 13;

const ProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    denyFixedConfiguration,
    selectButtonTitle,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
    // XNG-Changes
    disabled,
    hideActions,
    isPriceManagementPage,
    onClearProduct,
    showSearchIcon,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [qtyHasChanged, setQtyHasChanged] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    // XNG-Changes
    const [showRemoveIcon, setShowRemoveIcon] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState("");

    useEffect(() => {
        reset();
    }, [location.pathname]);

    // XNG-Changes
    useEffect(() => {
        if (isPriceManagementPage && !selectedProductInfo) {
            setSelectedProduct("");
        }
    }, [selectedProductInfo]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper {...styles.optionWrapper} data-test-selector={`productSelector_search_${product.id}`}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage {...styles.productImage} src={product.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText} data-test-selector="productSelector_ProductTitle">
                            {product.displayTitle || product.title}
                        </Typography>
                        <Typography {...styles.autocompleteErpText}>
                            {product.displayErpNumber || product.erpNumber}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        if (selectedProductInfo && !qtyHasChanged) {
            setQty(`${selectedProductInfo.qtyOrdered}`);
        }

        // XNG-Changes onSelectProduct when hideActions is true and product is selected
        if (hideActions && selectedProductInfo && product && !errorMessage) {
            const newProductInfo = { ...selectedProductInfo };
            newProductInfo.qtyOrdered = parseFloat(qty);
            onSelectProduct(newProductInfo, product);
        }
        // XNG-Changes: add product, hideActions
    }, [selectedProductInfo, product, hideActions]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    // XNG-Changes: update selectedProduct when variant product is selected from productSelectorVariantModal
    useEffect(() => {
        if (product) {
            setSelectedProduct(product.id);
        }
    }, [product]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            // XNG-Changes
            const selectedProduct = searchResults.find(o => o.id === value);
            const variantParentId = selectedProduct?.styleParentId;

            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
            }
            // XNG-Change
            setShowRemoveIcon(true);
            setSelectedProduct(value || "");
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setShowRemoveIcon(true);
        setSearchTerm(event.target.value);

        if (selectedProductInfo) {
            clearProduct();
        }

        debouncedSearchProducts(event.target.value);
    };

    const selectProduct = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = parseFloat(qty);

        onSelectProduct(newProductInfo, product);

        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "" });
        // XNG-Change
        setShowRemoveIcon(false);
        setSelectedProduct("");
    };

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({
                    searchTerm,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
                // XNG-Changes
            } else if (searchResults && searchResults.length > 0 && searchResults[0].id) {
                onSelectionChangeHandler(searchResults[0].id);
            }
        }
    };

    const clearButtonHandler = () => {
        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "" });
        // XNG-Changes
        setShowRemoveIcon(false);
        setErrorMessage("");
        onClearProduct?.();
        setSelectedProduct("");
    };

    return (
        // XNG-Changes: change structure - use StyledWrapper instead of GridWrapper and GridItem
        <StyledWrapper {...styles.container} data-test-selector="productSelector" {...{ hasError: !!errorMessage }}>
            <StyledWrapper {...styles.searchGridItem}>
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    // XNG-Change: add placeholder and change label
                    label={translate("Search Products")}
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={onKeyPress}
                    filterOption={() => true}
                    isLoading={isSearching}
                    options={options}
                    error={errorMessage}
                    data-test-selector="productSelector_search"
                    // XNG-Changes
                    selected={selectedProduct}
                    disabled={disabled}
                    showSearchIcon={showSearchIcon}
                    clearClickableProps={{
                        clickableProps: { onClick: () => clearButtonHandler() },
                    }}
                />
                {/* XNG-Changes:move clear icon inside DynamicDropdown  */}
                {/* {selectedProductInfo && <Icon {...styles.clearIcon} src={X} onClick={clearButtonHandler} />} */}
            </StyledWrapper>
            {/* XNG-Changes: add hideActions */}
            {!hideActions && (
                <>
                    <StyledWrapper {...styles.qtyGridItem} {...{ hasError: !!errorMessage }}>
                        {/* XNG-Changes */}
                        <QtyField
                            showLabel={false}
                            qtySizeVariant="large"
                            value={qty}
                            onChange={(e: any) => {
                                setQty(e.currentTarget.value);
                                setQtyHasChanged(true);
                            }}
                            data-test-selector="productSelector_qty"
                            extendedStyles={styles.qtyFieldStyle}
                            min={product?.minimumOrderQty}
                            multipleSaleQty={product?.detail?.multipleSaleQty}
                        />
                        {/* <TextField
                            type="number"
                            min="0"
                            label={translate("QTY")}
                            value={qty}
                            onChange={e => {
                                setQty(e.currentTarget.value);
                                setQtyHasChanged(true);
                            }}
                            data-test-selector="productSelector_qty"
                        /> */}
                    </StyledWrapper>
                    <StyledWrapper {...styles.unitOfMeasureGridItem} {...{ hasError: !!errorMessage }}>
                        {product && selectedProductInfo && (
                            <ProductContext.Provider
                                value={{
                                    product,
                                    productInfo: selectedProductInfo,
                                    onUnitOfMeasureChanged: uomChangeHandler,
                                }}
                            >
                                <ProductUnitOfMeasureSelect
                                    extendedStyles={styles.unitOfMeasureSelect}
                                    data-test-selector="productSelector_uom"
                                    // XNG-Changes: remove label to match with design
                                    labelOverride=""
                                />
                            </ProductContext.Provider>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonGridItem} {...{ hasError: !!errorMessage }}>
                        <Button
                            {...styles.selectButton}
                            onClick={() => selectProduct()}
                            disabled={!selectedProductInfo || !!errorMessage || Number(qty) < 1}
                            data-test-selector="productSelector_selectProduct"
                            // XNG-Changes
                            aria-label={selectButtonTitle || translate("Select Product")}
                        >
                            {selectButtonTitle || translate("Select Product")}
                        </Button>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
